<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    :id="id"
    ref="BasePage"
    :class="[bemBlock, 'the-home']"
    :data-page-id="pageId"
    :data-page-type="getCustomTypeComponent(pageData)"
    @click="handlerSearchableClick"
  >
    <div :class="[bemElement('modules'), 'the-home__modules']">
      <template v-for="(component, index) in filteredComponents">
        <div
          v-if="
            Object.values(customComponentType).includes(component.type_id) &&
            component.custom &&
            component.custom.data
          "
          :key="`component-custom-${component.id}-${index}`"
          :class="[bemElement('module'), 'the-home-module']"
        >
          <component
            :is="'style'"
            v-if="component.custom.data.styles"
            v-html="component.custom.data.styles"
          />
          <TheSearch
            v-if="component.type_id === customComponentType.searchForm"
            bem="the-search-ai-form"
            :class="[
              component.custom.data.componentBaseClassName,
              component.custom.data.componentClassName,
              `base-page-render__module--${
                component.custom.data.componentClassModifiers
              }`,
            ]"
            v-bind="getCustomDataComponent(component, searchProp)"
          />
          <ThePageComponentCategoryFeatured
            v-else-if="isCategoryFeatured(component)"
            :data="component.custom.data"
            :regions="component.regions"
            :category="component.custom.data.category"
            v-bind="getCustomDataComponent(component)"
          />
          <ThePageComponentRecommendedProducts
            v-else-if="
              [
                customComponentType.recommendedEinsteinProducts,
                customComponentType.recommendedProducts,
              ].includes(component.type_id)
            "
            :data="component.custom.data"
            :regions="component.regions"
            :is-recommendation-from-einstein="
              component.type_id ===
              customComponentType.recommendedEinsteinProducts
            "
            v-bind="getCustomDataComponent(component)"
          />
        </div>
        <div
          v-else-if="component.custom && component.custom.htmlRendered"
          :key="`component-${component.id}-${index}`"
          :class="[bemElement('module'), 'the-home-module']"
          v-html="component.custom.htmlRendered"
        />
        <div
          v-if="
            component.type_id === customComponentType.searchForm &&
            getSearchFormLoading
          "
          v-show="isLoadingSearch"
          :key="`component-loading-${component.id}-${index}`"
          :class="[
            bemElement('loading-search'),
            'the-home-module',
            getSearchFormLoading.className,
          ]"
          v-html="getSearchFormLoading.htmlRendered"
        />
      </template>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import isEqual from 'lodash/isEqual';
import TheSearch from './TheSearch.vue';
import ThePageComponentCategoryFeatured from './ThePageComponentCategoryFeatured.vue';
import ThePageComponentRecommendedProducts from './ThePageComponentRecommendedProducts.vue';
import bemClassesMixin from '../mixins/bemClassesMixin';
import EventBusConfig from '../config/eventBusConfig';
import getFrontConfig from '../../common/getFrontConfig';
import { urlWithSearchParams } from '../../common/utils/url';
import getAnalyticsEventBus from '../../common/getAnalyticsEventBus';

const analyticsEventBus = getAnalyticsEventBus();

const {
  urls: { getProductInfoList, getSerializePage },
} = getFrontConfig();

export default {
  name: 'BasePageRender',

  components: {
    ThePageComponentCategoryFeatured,
    ThePageComponentRecommendedProducts,
    TheSearch,
  },

  mixins: [bemClassesMixin],

  props: {
    pageId: {
      type: String,
      required: true,
    },
    queryParameters: {
      type: Object,
      default: () => ({}),
    },
    searchProp: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      customComponentType: {
        categoryFeatured: 'vue.categoryFeatured',
        recommendedEinsteinProducts: 'vue.recommendedEinsteinProducts',
        recommendedProducts: 'vue.recommendedProducts',
        searchForm: 'vue.searchForm',
      },
      componentsPage: [],
      id: `page-${this.pageId}`,
      pageData: {},
    };
  },

  computed: {
    ...mapState({
      results: (state) => state.search.results,
      isLoadingSearch: (state) => state.search.isLoading,
    }),
    getSearchFormLoading() {
      const {
        regions,
        custom: {
          data: { loadingAnimationClass },
        },
      } =
        this.componentsPage?.find(
          (comp) => comp.type_id === this.customComponentType.searchForm
        ) || {};
      const { htmlRendered } = regions?.[0]?.components?.[0]?.custom || {};
      return htmlRendered
        ? {
            className: loadingAnimationClass,
            htmlRendered,
          }
        : null;
    },
    filteredComponents() {
      if (!this.isLoadingSearch && !this.results?.productSearch)
        return this.componentsPage;
      // We need to filter components for the imageSearchResult view, without the rest of components.
      // We check for the first component as freeText and input for now.
      // if the first component configured is not a freeText will not rendered
      // TODO: Create attr in components for check visibility in image search results page.
      return this.componentsPage.filter(
        (
          { type_id },
          index // eslint-disable-line camelcase
        ) =>
          ['vue.searchForm'].includes(type_id) ||
          (type_id === 'vue.freeText' && index === 0)
      ); // eslint-disable-line camelcase
    },
  },

  created() {
    this.getPageData();
  },

  updated() {
    const target = this.$refs.BasePage;
    if (!target) return;
    this.$emit('on-loaded', {
      target,
      id: this.id,
      type: this.getCustomTypeComponent(this.pageData),
    });
  },

  methods: {
    getPageData() {
      fetch(
        urlWithSearchParams(getSerializePage, {
          ...this.queryParameters,
          id: this.pageId,
        })
      )
        .then((res) => res.json())
        .then(this.setPageData);
    },

    setPageData(data) {
      if (!data || isEqual(data, this.pageData)) return;
      this.pageData = data;
      this.componentsPage = this.pageData?.regions?.[0]?.components || [];
      this.setCategoryComponentData();
    },
    async setCategoryComponentData() {
      const component = this.componentsPage?.find(
        (comp) => comp.type_id === this.customComponentType.categoryFeatured
      );
      if (!component) return;

      const {
        custom: {
          data: {
            category: { id, name, productIds },
            styles,
            componentBaseClassName,
            componentClassName,
            titleClassName,
            posterClassName,
            sliderClassName,
            sliderLayout,
          },
        },
        regions = [],
      } = component;

      let title = null;
      let poster = null;
      regions?.forEach((region) => {
        region?.components?.forEach((child) => {
          const {
            custom: { htmlRendered },
          } = child;
          if (htmlRendered && region.id === 'poster') {
            poster = {
              class: posterClassName,
              html: htmlRendered,
            };
          }
          if (htmlRendered && region.id === 'title') {
            title = {
              class: titleClassName,
              html: htmlRendered,
            };
          }
        });
      });

      const slider = productIds
        ? {
            class: sliderClassName,
            products: await this.getProductsInfo(productIds),
            tileContext: sliderLayout,
          }
        : null;

      this.categoryComponent = {
        className: [componentBaseClassName, componentClassName],
        id,
        name,
        poster,
        title,
        slider,
        productIds,
        styles,
      };
    },

    async getProductsInfo(productsIds) {
      const url = urlWithSearchParams(getProductInfoList, {
        ids: productsIds.join(','),
      });
      const { products = [] } = await fetch(url).then((response) =>
        response.json()
      );
      return products;
    },

    isCategoryFeatured(component) {
      return (
        component.type_id === this.customComponentType.categoryFeatured &&
        component.custom?.data?.category?.id
      );
    },

    getCustomTypeComponent(component) {
      return (
        component?.custom?.data?.componentType || component?.type_id || null
      );
    },

    getCustomDataComponent(component, additionalData = {}) {
      return {
        'data-component-type': this.getCustomTypeComponent(component),
        'data-component-name': component.custom?.data?.componentName || null,
        'data-include-desktop':
          component.custom?.data?.includeDesktop ||
          component.data.includeDesktop,
        'data-include-mobile':
          component.custom?.data?.includeMobile || component.data.includeMobile,
        ...additionalData,
      };
    },

    handlerSearchableClick(e) {
      const { textContent } = e?.target?.closest('[href$="#searchable"]') || {};
      if (textContent) {
        e.preventDefault();
        this.$eventBus.$emit(
          EventBusConfig.commands.TheSearch.search,
          textContent
        );
        // Send info to Analytics
        analyticsEventBus.publish(
          analyticsEventBus.EVENTS.searchMostSearchedClicked,
          textContent
        );
      }
    },
  },
};
</script>
